import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { CheckCircle2, Download, ExternalLink } from 'lucide-react';
import { siteConfig } from '@/config/site';
import { jsPDF } from 'jspdf';

export function ReceiptModal({ isOpen, onClose, receiptData }) {
  if (!receiptData) return null;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('id-ID', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const handleDownload = () => {
    const doc = new jsPDF();
    
    // Set font
    doc.setFont('helvetica');
    
    // Title
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('HYTALE INDONESIA', 105, 20, { align: 'center' });
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Struk Pembelian / Receipt', 105, 28, { align: 'center' });
    
    // Line separator
    doc.setLineWidth(0.5);
    doc.line(20, 35, 190, 35);
    
    // Order Details
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Order ID:', 20, 45);
    doc.setFont('helvetica', 'normal');
    doc.text(receiptData.order_id, 60, 45);
    
    doc.setFont('helvetica', 'bold');
    doc.text('Tanggal:', 20, 55);
    doc.setFont('helvetica', 'normal');
    doc.text(formatDate(receiptData.completed_at), 60, 55);
    
    // Line separator
    doc.line(20, 62, 190, 62);
    
    // Purchase Details Header
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('DETAIL PEMBELIAN', 20, 72);
    
    // Purchase Details
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Produk:', 20, 82);
    doc.setFont('helvetica', 'normal');
    doc.text(receiptData.product_name, 60, 82);
    
    doc.setFont('helvetica', 'bold');
    doc.text('Username:', 20, 92);
    doc.setFont('helvetica', 'normal');
    doc.text(receiptData.username, 60, 92);
    
    doc.setFont('helvetica', 'bold');
    doc.text('Total Harga:', 20, 102);
    doc.setFontSize(14);
    doc.text(`Rp ${receiptData.amount?.toLocaleString('id-ID')}`, 60, 102);
    
    // Line separator
    doc.setLineWidth(0.5);
    doc.line(20, 110, 190, 110);
    
    // Status
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(34, 197, 94); // green
    doc.text('STATUS: PEMBAYARAN BERHASIL ✓', 105, 120, { align: 'center' });
    
    // Reset color
    doc.setTextColor(0, 0, 0);
    
    // Line separator
    doc.line(20, 127, 190, 127);
    
    // Thank you message
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Terima kasih telah berbelanja!', 105, 137, { align: 'center' });
    doc.text('Benefit telah diberikan ke akun kamu.', 105, 145, { align: 'center' });
    
    // Server Info
    doc.setFontSize(9);
    doc.text(`Server: ${siteConfig.serverIP}`, 105, 160, { align: 'center' });
    doc.text(`Discord: ${siteConfig.links.discord}`, 105, 167, { align: 'center' });
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text('Simpan struk ini sebagai bukti pembelian.', 105, 280, { align: 'center' });
    doc.text('Jika ada masalah, hubungi staff dengan menyertakan Order ID.', 105, 286, { align: 'center' });
    
    // Save PDF
    doc.save(`receipt-${receiptData.order_id}.pdf`);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[80%] max-w-[500px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-6 w-6 text-green-500" />
            Struk Pembelian
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Success Badge */}
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/20 border border-green-500/30">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
              <span className="font-semibold text-green-700 dark:text-green-400">
                PEMBAYARAN BERHASIL
              </span>
            </div>
          </div>

          {/* Receipt Details */}
          <div className="space-y-4 p-6 rounded-lg border-2 border-primary/20 bg-card">
            {/* Header */}
            <div className="text-center pb-4 border-b border-border">
              <h3 className="font-display text-xl font-bold mb-1">
                HYTALE INDONESIA
              </h3>
              <p className="text-xs text-muted-foreground">Struk Pembelian</p>
            </div>

            {/* Order Info */}
            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Order ID:</span>
                <span className="font-mono font-semibold">{receiptData.order_id}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Tanggal:</span>
                <span className="font-semibold">
                  {formatDate(receiptData.completed_at)}
                </span>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-dashed border-border my-4" />

            {/* Purchase Details */}
            <div className="space-y-3">
              <h4 className="font-semibold text-sm">Detail Pembelian:</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Produk:</span>
                  <span className="font-semibold">{receiptData.product_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Username:</span>
                  <span className="font-semibold">{receiptData.username}</span>
                </div>
                <div className="flex justify-between text-sm pt-2 border-t border-border">
                  <span className="text-muted-foreground font-semibold">Total Harga:</span>
                  <span className="font-bold text-lg text-primary">
                    Rp {receiptData.amount?.toLocaleString('id-ID')}
                  </span>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="pt-4 border-t border-border text-center">
              <p className="text-xs text-muted-foreground">
                Terima kasih telah berbelanja!
                <br />
                Benefit telah diberikan ke akun kamu.
              </p>
            </div>
          </div>

          {/* Info Message */}
          <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
            <p className="text-xs text-blue-700 dark:text-blue-400 text-center">
              Simpan struk ini sebagai bukti pembelian. Jika ada masalah, hubungi staff dengan menyertakan Order ID.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              onClick={handleDownload}
              variant="outline"
              className="flex-1"
            >
              <Download className="h-4 w-4 mr-2" />
              Download Struk
            </Button>
            <Button
              asChild
              className="flex-1"
            >
              <a
                href={siteConfig.links.discord}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2"
              >
                Hubungi Staff
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
